(() => {
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const state = {
    overlays: new Map(), // connectorId -> { rootEl, overlayEl, svg, bgPath, drawPath, maskPath }
    ticking: false
  };

  const qs = (sel, root = document) => root.querySelector(sel);
  const clamp01 = (n) => Math.max(0, Math.min(1, n));

  const hashSeed = (str) => {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return (h >>> 0);
  };

  const rand01 = (seed) => {
    let x = seed >>> 0;
    x ^= x << 13; x >>>= 0;
    x ^= x >> 17; x >>>= 0;
    x ^= x << 5;  x >>>= 0;
    return (x >>> 0) / 4294967296;
  };

  const getAnchorPoint = (el, anchor) => {
    const r = el.getBoundingClientRect();
    switch (anchor) {
      case "top": return { x: r.left + r.width / 2, y: r.top };
      case "bottom": return { x: r.left + r.width / 2, y: r.bottom };
      case "left": return { x: r.left, y: r.top + r.height / 2 };
      case "right": return { x: r.right, y: r.top + r.height / 2 };
      case "center":
      default: return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
  };

  // Curve that still looks curved when points are vertically aligned
  const buildCurveD = (p1, p2, curve) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    if (ady >= adx) {
      const bend = curve * Math.sign(dx || 1);
      const c1 = { x: p1.x + bend, y: p1.y + dy * 0.33 };
      const c2 = { x: p2.x - bend, y: p1.y + dy * 0.66 };
      return `M ${p1.x} ${p1.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p2.x} ${p2.y}`;
    }

    const bend = curve * Math.sign(dy || 1);
    const c1 = { x: p1.x + dx * 0.33, y: p1.y + bend };
    const c2 = { x: p1.x + dx * 0.66, y: p2.y - bend };
    return `M ${p1.x} ${p1.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p2.x} ${p2.y}`;
  };

  // Squiggle: wave-ish but tapered + slight noise (your existing "squiggle")
  const buildSquiggleD = (p1, p2, amp, freq, seedStr) => {
    const seedBase = hashSeed(seedStr);

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.hypot(dx, dy) || 1;

    const ux = dx / dist;
    const uy = dy / dist;

    const px = -uy;
    const py = ux;

    const n = Math.max(10, Math.min(120, Math.round(freq * 10)));

    const phase = rand01(seedBase) * Math.PI * 2;
    const noiseAmt = 0.35 + rand01(seedBase + 1) * 0.35;

    const pts = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;

      const bx = p1.x + dx * t;
      const by = p1.y + dy * t;

      const taper = Math.sin(Math.PI * t);
      const w = Math.sin((Math.PI * 2 * freq * t) + phase);

      const noise = (rand01(seedBase + 1000 + i) - 0.5) * 2;

      const off = (w + noise * noiseAmt) * amp * taper;

      pts.push({
        x: bx + px * off,
        y: by + py * off
      });
    }

    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length - 1; i++) {
      const cx = pts[i].x;
      const cy = pts[i].y;
      const mx = (pts[i].x + pts[i + 1].x) / 2;
      const my = (pts[i].y + pts[i + 1].y) / 2;
      d += ` Q ${cx} ${cy}, ${mx} ${my}`;
    }
    const last = pts[pts.length - 1];
    d += ` T ${last.x} ${last.y}`;
    return d;
  };

  // Organic: seeded multi-octave noise + drift => roams across viewport (not regular wave)
  const buildOrganicD = (p1, p2, chaosPct, points, smoothPct, seedStr) => {
    const seedBase = hashSeed(seedStr);

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.hypot(dx, dy) || 1;

    const ux = dx / dist;
    const uy = dy / dist;

    const px = -uy;
    const py = ux;

    const chaos = (chaosPct / 100);
    const smooth = (smoothPct / 100);

    // Viewport-scaled amplitude (this is what makes it feel truly "chaotic")
    const vw = Math.max(window.innerWidth || 0, 1);

    let baseAmp = vw * 0.45 * chaos;

    // If endpoints are mostly vertical, boost amplitude (your case using section wrappers)
    const mostlyVertical = Math.abs(dx) < Math.abs(dy) * 0.35;
    if (mostlyVertical) baseAmp *= 1.25;

    baseAmp = Math.min(baseAmp, vw * 0.65);

    // 1D value noise
    const valueNoise1D = (t, seed) => {
      const x = t * 1000;
      const i0 = Math.floor(x);
      const f = x - i0;
      const u = f * f * (3 - 2 * f);

      const r0 = (rand01(seed + i0) - 0.5) * 2;
      const r1 = (rand01(seed + i0 + 1) - 0.5) * 2;

      return r0 * (1 - u) + r1 * u;
    };

    // Fractal-ish noise (fbm)
    const fbm = (t) => {
      let sum = 0;
      let amp = 1;
      let freq = 1;
      let norm = 0;

      for (let o = 0; o < 4; o++) {
        sum += valueNoise1D(t * freq, seedBase + o * 99991) * amp;
        norm += amp;
        amp *= 0.55;
        freq *= 2.2;
      }
      return sum / (norm || 1);
    };

    // Build points (this is the part you were trying to splice)
    const pts = [];
    const nPts = Math.max(6, Math.min(60, Math.round(points)));

    for (let i = 0; i <= nPts; i++) {
      const t = i / nPts;

      const bx = p1.x + dx * t;
      const by = p1.y + dy * t;

      const taper = Math.sin(Math.PI * t); // anchor at ends

      const n1 = fbm(t);
      const n2 = fbm(t + 7.13);

      // slow drifting component (meander)
      const drift = fbm(t + 31.7) * baseAmp * 0.35;

      // perpendicular wander
      const offP = (n1 * 0.9 + n2 * 0.35) * baseAmp * taper;

      // along-path wander
      const offU = drift * taper;

      // extra slow swerve
      const swerve = fbm(t + 120.4) * baseAmp * 0.18 * taper;

      pts.push({
        x: bx + px * offP + ux * offU + px * swerve,
        y: by + py * offP + uy * offU + py * swerve
      });
    }

    // Catmull-Rom -> Cubic Beziers
    const catmullRomToBezier = (p0, p1i, p2i, p3, tension) => {
      const t = tension;
      const c1 = {
        x: p1i.x + (p2i.x - p0.x) / 6 * t,
        y: p1i.y + (p2i.y - p0.y) / 6 * t
      };
      const c2 = {
        x: p2i.x - (p3.x - p1i.x) / 6 * t,
        y: p2i.y - (p3.y - p1i.y) / 6 * t
      };
      return { c1, c2 };
    };

    // Higher smoothPct => lower tension (smoother curve)
    const tension = 0.25 + (1 - smooth) * 0.9;

    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(0, i - 1)];
      const p1i = pts[i];
      const p2i = pts[i + 1];
      const p3 = pts[Math.min(pts.length - 1, i + 2)];

      const { c1, c2 } = catmullRomToBezier(p0, p1i, p2i, p3, tension);
      d += ` C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p2i.x} ${p2i.y}`;
    }

    return d;
  };

  const createOverlay = (connectorId, zIndex, layer) => {
    const wrap = document.createElement("div");
    wrap.className = "smart-connector-overlay";
    wrap.setAttribute("data-connector-overlay", connectorId);

    const maskId = `sc-mask-${connectorId}`;

    wrap.innerHTML = `
      <svg viewBox="0 0 ${window.innerWidth} ${window.innerHeight}" preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <defs>
          <mask id="${maskId}">
            <rect x="0" y="0" width="100%" height="100%" fill="black"></rect>
            <path class="smart-connector-path smart-connector-path--mask" stroke="white" fill="none"></path>
          </mask>
        </defs>

        <path class="smart-connector-path smart-connector-path--bg" fill="none"></path>
        <path class="smart-connector-path smart-connector-path--draw" fill="none" mask="url(#${maskId})"></path>
      </svg>
    `;

    document.body.appendChild(wrap);

    const svg = wrap.querySelector("svg");
    const bgPath = wrap.querySelector(".smart-connector-path--bg");
    const drawPath = wrap.querySelector(".smart-connector-path--draw");
    const maskPath = wrap.querySelector(".smart-connector-path--mask");

    // set initial layer
    const finalZ = (layer === "behind") ? 0 : (zIndex || 50);
    wrap.style.zIndex = String(finalZ);
    wrap.setAttribute("data-layer", layer || "above");

    return { wrap, svg, bgPath, drawPath, maskPath };
  };

  const setViewBoxToViewport = (svg) => {
    svg.setAttribute("viewBox", `0 0 ${window.innerWidth} ${window.innerHeight}`);
  };

  // Progress is based on an adjustable "read line" in the viewport.
  // readLinePct: 0 (top) .. 100 (bottom). 50 = center.
    const calcProgressStartToEnd = (startEl, endEl, readLinePct = 50) => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

    const startRect = startEl.getBoundingClientRect();
    const endRect = endEl.getBoundingClientRect();

    const startY = startRect.top + scrollTop;
    const endY   = endRect.top + scrollTop;

    const readLineY = scrollTop + (window.innerHeight * (readLinePct / 100));

    const denom = Math.max(1, endY - startY);
    return clamp01((readLineY - startY) / denom);
  };

  const calcProgressPage = () => {
    const scrollTop = (document.body.scrollTop + document.documentElement.scrollTop) || 0;
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (scrollHeight <= 0) return 0;
    return clamp01(scrollTop / scrollHeight);
  };

  const syncOne = (connectorId) => {
    const item = state.overlays.get(connectorId);
    if (!item) return;

    const root = item.rootEl;

    // Dynamic layer support (theme editor changes should apply immediately)
    const layer = root.dataset.layer || "above";
    const z = Number(root.dataset.z || 50);
    item.overlayEl.style.zIndex = String(layer === "behind" ? 0 : z);
    item.overlayEl.setAttribute("data-layer", layer);

    // For single connector testing; if you have multiple connectors mixed, we can make this "any behind"
    document.documentElement.classList.toggle("sc-connector-behind", layer === "behind");

    const startSel = root.dataset.start;
    const endSel = root.dataset.end;

    const anchor = root.dataset.anchor || "center";
    const curve = Number(root.dataset.curve || 80);
    const offsetX = Number(root.dataset.offsetX || 0);
    const offsetY = Number(root.dataset.offsetY || 0);

    const drawMode = root.dataset.drawMode || "between"; // "page" or "between"
    const pathStyle = root.dataset.pathStyle || "curve"; // "curve" | "squiggle" | "organic"

    const sqAmp = Number(root.dataset.sqAmp || 40);
    const sqFreq = Number(root.dataset.sqFreq || 8);

    const orgChaos = Number(root.dataset.orgChaos || 60);
    const orgPoints = Number(root.dataset.orgPoints || 16);
    const orgSmooth = Number(root.dataset.orgSmooth || 70);

    const drawOpacity = Number(root.dataset.drawOpacity || 100) / 100;
    const bgOpacity = Number(root.dataset.bgOpacity || 55) / 100;

    const startEl = startSel ? qs(startSel) : null;
    const endEl = endSel ? qs(endSel) : null;

    if (!startEl || !endEl) {
      item.overlayEl.style.display = "none";
      return;
    }
    item.overlayEl.style.display = "";

    setViewBoxToViewport(item.svg);

    const p1 = getAnchorPoint(startEl, anchor);
    const p2 = getAnchorPoint(endEl, anchor);

    const a = { x: p1.x + offsetX, y: p1.y + offsetY };
    const b = { x: p2.x + offsetX, y: p2.y + offsetY };

    let d;
    if (pathStyle === "organic") {
      d = buildOrganicD(a, b, orgChaos, orgPoints, orgSmooth, connectorId);
    } else if (pathStyle === "squiggle") {
      d = buildSquiggleD(a, b, sqAmp, sqFreq, connectorId);
    } else {
      d = buildCurveD(a, b, curve);
    }

    item.bgPath.setAttribute("d", d);
    item.drawPath.setAttribute("d", d);
    item.maskPath.setAttribute("d", d);

    // Styles
    const stroke = root.dataset.stroke || "#1085c7";
    const strokeWidth = Number(root.dataset.strokeWidth || 4);
    const dash = (root.dataset.dash || "20 10").trim();

    const bgStroke = root.dataset.bgStroke || "#ffffff";
    const bgStrokeWidth = Number(root.dataset.bgStrokeWidth || 6);

    // BG path (dashed guide)
    item.bgPath.style.stroke = bgStroke;
    item.bgPath.style.strokeWidth = String(bgStrokeWidth);
    item.bgPath.style.strokeDasharray = dash;
    item.bgPath.style.opacity = String(bgOpacity);

    // Dashed draw path (revealed by mask)
    item.drawPath.style.stroke = stroke;
    item.drawPath.style.strokeWidth = String(strokeWidth);
    item.drawPath.style.strokeDasharray = dash;
    item.drawPath.style.strokeDashoffset = "0";
    item.drawPath.style.opacity = String(drawOpacity);

    // Mask path reveals the dashed draw path
    const maskWidth = strokeWidth + 10;
    item.maskPath.style.strokeWidth = String(maskWidth);
    item.maskPath.style.strokeLinecap = "round";
    item.maskPath.style.strokeLinejoin = "round";

    let length = 0;
    try {
      length = item.maskPath.getTotalLength();
    } catch (e) {
      return;
    }

    item.maskPath.style.strokeDasharray = `${length}`;
    item.maskPath.style.strokeDashoffset = `${length}`;

    if (prefersReduced) {
      item.maskPath.style.strokeDashoffset = "0";
      return;
    }

    const readLine = Number(root.dataset.readLine || 50);

    let progress = (drawMode === "page")
      ? calcProgressPage()
      : calcProgressStartToEnd(startEl, endEl, readLine);

    /* Optional: keep the drawing centered in the viewport longer.
      Higher band = more “centered” feeling; lower band = more immediate. */
    const startDelay = 0.08; // 8% delay before it starts drawing
    const endDelay = 0.02;   // 2% reserve so it finishes right at the end

    progress = clamp01((progress - startDelay) / Math.max(0.0001, 1 - startDelay - endDelay));

const draw = length * progress;
item.maskPath.style.strokeDashoffset = `${length - draw}`;
}; // ✅ closes syncOne()

  const syncAll = () => {
    state.ticking = false;
    for (const connectorId of state.overlays.keys()) {
      syncOne(connectorId);
    }
  };

  const requestSync = () => {
    if (state.ticking) return;
    state.ticking = true;
    requestAnimationFrame(syncAll);
  };

  const mountOne = (rootEl) => {
    const connectorId = rootEl.dataset.connectorId;
    if (!connectorId || state.overlays.has(connectorId)) return;

    const z = Number(rootEl.dataset.z || 50);
    const layer = rootEl.dataset.layer || "above";

    const overlay = createOverlay(connectorId, z, layer);

    state.overlays.set(connectorId, {
      rootEl,
      overlayEl: overlay.wrap,
      svg: overlay.svg,
      bgPath: overlay.bgPath,
      drawPath: overlay.drawPath,
      maskPath: overlay.maskPath
    });

    requestSync();
  };

  const unmountOne = (rootEl) => {
    const connectorId = rootEl?.dataset?.connectorId;
    if (!connectorId) return;

    const item = state.overlays.get(connectorId);
    if (!item) return;

    item.overlayEl.remove();
    state.overlays.delete(connectorId);
  };

  const boot = () => {
    document.querySelectorAll("[data-smart-connector]").forEach(mountOne);

    window.addEventListener("scroll", requestSync, { passive: true });
    window.addEventListener("resize", requestSync, { passive: true });

    requestSync();
  };

  document.addEventListener("DOMContentLoaded", boot);

  document.addEventListener("shopify:section:load", (e) => {
    const root = e.target?.querySelector?.("[data-smart-connector]");
    if (root) mountOne(root);
  });

  document.addEventListener("shopify:section:unload", (e) => {
    const root = e.target?.querySelector?.("[data-smart-connector]");
    if (root) unmountOne(root);
  });
})();